import { API_ROUTES, postEncryptedJson } from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { toApiDateParam } from "../../utils/dateRangeApiFormat";

/**
 * Pending Transactions Controller
 * Uses GET_ALL_TRANSACTIONS endpoint with STATUS filter (like Flutter's pending_transactions_screen.dart)
 * STATUS: 1 = PENDING (TransactionStatus.PENDING.value in Flutter)
 */
class PendingTransactionsController {
  static _inFlight = new Map();
  static _cache = new Map();
  static _cacheTtlMs = 20 * 1000;

  /**
   * Fetch pending transactions from backend with encryption handled by apiClient.
   *
   * @param {Object} params
   * @param {number} [params.page=0] - Zero-based page index (also sent as ?page=).
   * @param {number} [params.perPage=10] - Items per page.
   * @param {number} [params.type=-1] - Transaction type filter (-1 = all, others backend-specific).
   * @param {string|null} [params.dateFrom=null] - ISO-like string "YYYY-MM-DDTHH:mm:ss.SSS" or null.
   * @param {string|null} [params.dateTo=null] - ISO-like string "YYYY-MM-DDTHH:mm:ss.SSS" or null.
   * @param {string|null} [params.search=null] - Free-text search.
   * @param {number|string|null} [params.currency=null] - Currency filter (backend-specific).
   * @param {number|string|null} [params.status=-1] - Status filter (-1 = all, 1 = pending, 7 = pending_user).
   * @param {number|string|null} [params.walletId=null] - Wallet filter.
   * @param {Object} [options] - Optional postEncryptedJson options (e.g., baseURL override).
   *
   * @returns {Promise<{
   *   items: Array,
   *   pagination: { page: number, perPage: number, total?: number, lastPage?: number },
   *   raw: any
   * }>}
   */
  static async getPendingTransactions(
    {
      page = 0,
      perPage = 10,
      type = -1,
      dateFrom = null,
      dateTo = null,
      search = null,
      currency = null,
      status = 1, // Default to PENDING status (TransactionStatus.PENDING = 1)
      walletId = null,
    } = {},
    options = {}
  ) {
    const apiDateFrom = toApiDateParam(dateFrom, false);
    const apiDateTo = toApiDateParam(dateTo, true);

    const requestKey = JSON.stringify({
      page,
      perPage,
      type,
      dateFrom: apiDateFrom,
      dateTo: apiDateTo,
      search: search || null,
      currency: currency ?? null,
      status: status ?? null,
      walletId: walletId ?? null,
      baseURL: options?.baseURL || "",
    });

    const cached = PendingTransactionsController._cache.get(requestKey);
    if (
      cached &&
      typeof cached.timestamp === "number" &&
      Date.now() - cached.timestamp < PendingTransactionsController._cacheTtlMs
    ) {
      return cached.value;
    }

    const inFlight = PendingTransactionsController._inFlight.get(requestKey);
    if (inFlight) {
      return await inFlight;
    }

    const run = async () => {
    // Laravel uses 1-based pagination; UI uses 0-based page index
    const endpoint = `${API_ROUTES.GET_ALL_TRANSACTIONS}?page=${page + 1}`;
    
    // Build body exactly like Flutter's transaction_history_controller.dart
    // Flutter sends: "TYPE": transactionTypeIndex == null ? null : transactionTypeIndex!.value
    // TransactionType.ALL.value = -1, so ALL sends -1 (not null)
    const body = {
      PER_PAGE: perPage,
      TYPE: type, // Send as-is: -1 for ALL, or specific type value
      DATE_FROM: apiDateFrom,
      DATE_TO: apiDateTo,
      SEARCH: (search === null || search === '') ? null : search,
      CURRENCY: currency ?? null,
      STATUS: status, // 1 = PENDING (TransactionStatus.PENDING.value)
      WALLET_ID: walletId ?? null,
    };


    const res = await postEncryptedJson(endpoint, body, options);
    const d = res.decryptedData || res.data || {};


    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg = d.MESSAGE || d.message || `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    // Server returns CODE 200 and DATA as a Laravel paginator object:
    // {
    //   current_page: 1,
    //   data: [ ...transactions... ],
    //   per_page: 10,
    //   total: 32,
    //   last_page: 4,
    //   ...
    // }
    const dataBlock = d.DATA || {};
    let list = [];

    if (Array.isArray(dataBlock.data)) {
      list = dataBlock.data;
    } else if (Array.isArray(dataBlock.DATA)) {
      list = dataBlock.DATA;
    } else if (Array.isArray(dataBlock.items)) {
      list = dataBlock.items;
    } else if (Array.isArray(dataBlock.transactions)) {
      list = dataBlock.transactions;
    } else if (Array.isArray(dataBlock.list)) {
      list = dataBlock.list;
    }

    // Normalize a minimal view model for the UI but keep raw item too
    const items = list.map((item) => {
      const id =
        item.TRANSACTION_ID ??
        item.REF_ID ??
        item.REFID ??
        item.REFERENCE_ID ??
        item.ID ??
        item.id ??
        null;

      const date =
        item.TRANSACTION_DATE ||
        item.TRX_DATE ||
        item.CREATED_AT ||
        item.DATE ||
        item.date ||
        null;

      const typeLabel =
        item.TYPE_LABEL ||
        item.TYPE_NAME ||
        item.TRANSACTION_TYPE ||
        item.TYPE ||
        item.type ||
        null;

      const amount =
        item.AMOUNT ??
        item.amount ??
        item.VALUE ??
        null;

      const balanceAfter =
        item.BALANCE_AFTER ??
        item.BALANCE ??
        item.CURRENT_BALANCE ??
        item.balance ??
        null;

      const statusLabel =
        item.STATUS_LABEL ||
        item.STATUS_NAME ||
        item.STATUS ||
        item.status ||
        null;

      return {
        id,
        date,
        typeLabel,
        amount,
        balanceAfter,
        statusLabel,
        raw: item,
      };
    });

    // Convert Laravel 1-based pagination to 0-based indexes for the UI
    const currentPage = Number(dataBlock.current_page ?? 1);
    const lastPage = Number(dataBlock.last_page ?? 1);
    const perPageVal = Number(dataBlock.per_page ?? perPage) || perPage;
    const totalVal = Number(dataBlock.total ?? dataBlock.TOTAL ?? 0);

    const pagination = {
      page: currentPage - 1,
      perPage: perPageVal,
      total: totalVal,
      lastPage: lastPage - 1,
    };

    const result = {
      items,
      pagination,
      raw: d,
    };
    PendingTransactionsController._cache.set(requestKey, {
      timestamp: Date.now(),
      value: result,
    });
    return result;
    };

    const promise = run();
    PendingTransactionsController._inFlight.set(requestKey, promise);
    try {
      return await promise;
    } finally {
      PendingTransactionsController._inFlight.delete(requestKey);
    }
  }

  /**
   * Cancel a pending transaction (DEPOSIT or WITHDRAWAL only).
   * Mirrors Flutter: transaction_screen.dart -> _cancelRequest()
   * Endpoint: api/v1/user/cancel-pending-transaction (APIURI.CANCEL_TRANSACTIONS)
   *
   * @param {string} transactionId - The TRANSACTION_ID to cancel.
   * @param {Object} [options] - postEncryptedJson options.
   * @returns {Promise<{ success: boolean, message: string, raw: any }>}
   */
  static async cancelPendingTransaction(transactionId, options = {}) {
    if (!transactionId) {
      throw new Error("TRANSACTION_ID is required");
    }

    const body = {
      TRANSACTION_ID: transactionId,
    };

    const res = await postEncryptedJson(
      API_ROUTES.CANCEL_PENDING_TRANSACTION,
      body,
      options
    );
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg =
        d.MESSAGE || d.message || `Cancel failed (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    return {
      success: true,
      message: d.MESSAGE || d.message || "Transaction cancelled successfully",
      raw: d,
    };
  }
}

export default PendingTransactionsController;
